import { Button } from '@emailforge/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Compliance banner for the marketing site. Shown once until the visitor picks
 * Accept or Reject; the choice is persisted in localStorage so refreshes and
 * future visits stay quiet.
 *
 * The marketing site does not actually load any third-party trackers — see
 * `/privacy`. The banner exists to satisfy ePrivacy/GDPR's "informed consent"
 * for the localStorage we DO use (locale + access/refresh tokens once
 * authenticated). Reject = same behavior, just an explicit record of refusal.
 */

const STORAGE_KEY = 'emailforge.cookieConsent';

type Consent = 'accepted' | 'rejected';

function readStoredConsent(): Consent | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'rejected' ? v : null;
  } catch {
    return null;
  }
}

function writeStoredConsent(value: Consent) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
    window.dispatchEvent(new CustomEvent('emailforge:cookie-consent', { detail: value }));
  } catch {
    // ignore — private mode etc.
  }
}

export function CookieBanner() {
  // Default to "decided" so we don't flash the banner during hydration on
  // returning visitors. The effect below switches it on if there's no stored
  // choice yet.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (readStoredConsent() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    writeStoredConsent('accepted');
    setVisible(false);
  };
  const reject = () => {
    writeStoredConsent('rejected');
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        maxWidth: 720,
        marginInline: 'auto',
        padding: 16,
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-3)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
        zIndex: 50,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 14,
        alignItems: 'center',
      }}
    >
      <div style={{ flex: '1 1 320px', minWidth: 240, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
        We use a small amount of <strong>browser storage</strong> for sign-in and language preference. No marketing
        cookies, no third-party trackers.{' '}
        <Link to="/privacy" style={{ color: 'var(--flame-2)' }}>
          Privacy details
        </Link>
        .
      </div>
      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
        <Button size="sm" onClick={reject}>
          Reject
        </Button>
        <Button variant="flame" size="sm" onClick={accept}>
          Accept
        </Button>
      </div>
    </div>
  );
}

/**
 * Small helper for any future code that needs to gate something on consent.
 * Today nothing reads this, but having the contract documented avoids ad-hoc
 * `localStorage.getItem` calls scattered around the app later.
 */
export function getCookieConsent(): Consent | null {
  return readStoredConsent();
}
